import { z } from "zod";

// A control as listed by Optro for import selection. Mirrors the backend's
// `ExternalControlInstance` wire shape (`GET /controls/external/controls`);
// process/owner/frequency come back inconsistently across Optro
// configurations, so they render as "–" in the table when null.
export const OptroControlSummarySchema = z.object({
  externalRef: z.string(),
  idCode: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  source: z.string(),
  linked: z.boolean(),
  // Queued or processing in an Optro import someone in the workspace
  // started. Listed disabled with an "Importing" badge. Absent from older
  // backends, so it defaults to false.
  importing: z.boolean().default(false),
  process: z.string().nullable(),
  owner: z.string().nullable(),
  frequency: z.string().nullable(),
});

export type OptroControlSummary = z.infer<typeof OptroControlSummarySchema>;

export const ListOptroControlsResponseSchema = z.object({
  controls: z.array(OptroControlSummarySchema),
  source: z.string().optional(),
  total: z.number(),
  hasMore: z.boolean(),
  nextOffset: z.number(),
});

export type ListOptroControlsResponse = z.infer<
  typeof ListOptroControlsResponseSchema
>;

// A single selectable filter option (process or owner) for the Optro import
// select step's filters.
export const OptroFilterOptionSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export type OptroFilterOption = z.infer<typeof OptroFilterOptionSchema>;

export const OptroFilterOptionsSchema = z.object({
  processes: z.array(OptroFilterOptionSchema),
  owners: z.array(OptroFilterOptionSchema),
});

export type OptroFilterOptions = z.infer<typeof OptroFilterOptionsSchema>;

// A workpaper attached to a control during import review. `source` records
// whether it came from Optro's linked workpapers or a local upload. `id`
// encodes the candidate's origin as produced by the backend's workpaper
// matcher: `att-{attachmentId}` | `file-{fileId}` | `sheet-{testId}`, or a
// presigned `uploadId` for local uploads. `fileId` is the Optro FILE id
// needed to download content; absent (-> null) for sheet candidates and for
// local uploads.
export const OptroWorkpaperFileSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  source: z.enum(["optro", "upload"]),
  period: z.string().nullable(),
  fileId: z.number().nullable().default(null),
});

export type OptroWorkpaperFile = z.infer<typeof OptroWorkpaperFileSchema>;

export const OptroWorkpaperMatchSchema = z.object({
  controlExternalRef: z.string(),
  workpapers: z.array(OptroWorkpaperFileSchema),
});

export type OptroWorkpaperMatch = z.infer<typeof OptroWorkpaperMatchSchema>;

// Per-control status for the agentic workpaper match job. Covers both the
// POST start response's `cached` list and the GET status endpoint's `items`
// list — the backend uses one shared shape for both.
export const MatchItemStatusSchema = z.enum([
  "queued",
  "crawling",
  "matched",
  "no_match",
  "failed",
]);

export type MatchItemStatus = z.infer<typeof MatchItemStatusSchema>;

export const MatchStatusItemSchema = z.object({
  controlExternalRef: z.string(),
  status: MatchItemStatusSchema,
  workpapers: z.array(OptroWorkpaperFileSchema),
  rationale: z.string().nullable(),
});

export type MatchStatusItem = z.infer<typeof MatchStatusItemSchema>;

// `POST /controls/external/match-workpapers/agentic` (202). `cached` covers
// any ref with a fresh Redis result — those refs are resolved immediately
// and, per the backend contract, will NEVER appear in a later status poll's
// `items` list. The FE must merge `cached` with polled `items` itself.
export const StartMatchWorkpapersAgenticResponseSchema = z.object({
  matchId: z.string(),
  cached: z.array(MatchStatusItemSchema),
});

export type StartMatchWorkpapersAgenticResponse = z.infer<
  typeof StartMatchWorkpapersAgenticResponseSchema
>;

// `GET /controls/external/match/{matchId}/status`. `isDone: true` with an
// empty `items` array means every requested ref was already covered by the
// POST's `cached` list — treat it as done, not as "not started".
export const MatchStatusResponseSchema = z.object({
  id: z.string(),
  isDone: z.boolean(),
  items: z.array(MatchStatusItemSchema),
});

export type MatchStatusResponse = z.infer<typeof MatchStatusResponseSchema>;

// The `detail` body of the 502 raised by the agentic start endpoint when
// some (but not all) refs failed to enqueue: the match still has a valid id
// and is worth polling for the refs that did enqueue. `cached` carries the
// same already-resolved items the 202 response's `cached` would have — a ref
// resolved from a previous job must not be lost (and mislabeled no_match by
// `resolveStuckAsNoMatch`) just because some unrelated ref failed to enqueue.
export const OptroMatchPartialEnqueueSchema = z.object({
  error: z.literal("optro_match_partial_enqueue"),
  matchId: z.string(),
  enqueued: z.number(),
  failed: z.number(),
  cached: z.array(MatchStatusItemSchema),
});

export type OptroMatchPartialEnqueue = z.infer<
  typeof OptroMatchPartialEnqueueSchema
>;

// A status this bundle doesn't know (the backend added one) degrades that
// one item to "failed" instead of failing the whole listing parse.
export const OptroImportItemStatusSchema = z
  .enum([
    "queued",
    "processing",
    "complete",
    "no_workpaper",
    "failed",
    "timed_out",
    "cancelled",
  ])
  .catch("failed");

export type OptroImportItemStatus = z.infer<typeof OptroImportItemStatusSchema>;

// Which of the import run's tasks an in-flight item is on — refines a
// `processing` status. Optional: only the work-step import panel shows it.
export const OptroImportStageSchema = z.enum([
  "queued",
  "fetching",
  "acquiring_documents",
  "configuring",
  "processing",
  "complete",
  "failed",
]);

export type OptroImportStage = z.infer<typeof OptroImportStageSchema>;

export const OptroImportItemSchema = z.object({
  controlExternalRef: z.string(),
  controlName: z.string(),
  status: OptroImportItemStatusSchema,
  // An unknown stage is dropped for the same reason an unknown status degrades.
  stage: OptroImportStageSchema.optional().catch(undefined),
  // Why a failed, timed-out or cancelled item stopped, in words for the user.
  failureReason: z.string().nullable().optional(),
});

export type OptroImportItem = z.infer<typeof OptroImportItemSchema>;

export const OptroImportJobSchema = z.object({
  id: z.string(),
  items: z.array(OptroImportItemSchema),
  isDone: z.boolean(),
});

export type OptroImportJob = z.infer<typeof OptroImportJobSchema>;

// One import from `GET /controls/external/imports` or
// `/work-steps/external/imports` (the Optro import registry). Items carry the
// per-import status fields plus the display summary sent when it started,
// which rebuilds placeholder rows after a refresh.
const OptroImportListingItemSchema = OptroImportItemSchema.extend({
  display: z.unknown().optional(),
});

export type OptroImportListingItem = z.infer<
  typeof OptroImportListingItemSchema
>;

const OptroImportListingJobSchema = z.object({
  id: z.string(),
  kind: z.enum(["control", "work_step"]),
  createdAt: z.string(),
  // null: a delta job that carries only the rows changed since the cursor, so
  // completion is worked out from the rows once merged.
  isDone: z.boolean().nullable(),
  items: z.array(OptroImportListingItemSchema),
});

export type OptroImportListingJob = z.infer<typeof OptroImportListingJobSchema>;

// `cursor` (epoch ms, opaque) is the `since` for the next poll; `snapshot` is
// false for a delta and true for a full listing. Both are nullish so a backend
// without cursors, or one that sends null, still parses.
export const OptroImportListingResponseSchema = z.object({
  imports: z.array(OptroImportListingJobSchema),
  cursor: z.string().nullish(),
  snapshot: z.boolean().nullish(),
});

// What the wizard hands to the import job for each selected control.
export interface OptroImportStartItem {
  control: OptroControlSummary;
  workpapers: OptroWorkpaperFile[];
}

// Connection details submitted from the "Configure Optro" dialog.
export interface OptroConnectionConfig {
  baseUrl: string;
  defaultWorkspaceId: string;
  apiKey: string;
}

// One chosen workpaper source, in the shape `POST /controls/external/import`
// expects for a single item's `workpapers[]` entry.
export interface OptroImportWorkpaperChoice {
  kind: "optro_attachment" | "optro_sheet" | "upload";
  attachmentId: number | null;
  testId: number | null;
  uploadId: string | null;
  fileId: number | null;
  fileName: string | null;
}

// One control to import, with its chosen workpaper(s) — the request body
// item shape for `POST /controls/external/import`.
export interface OptroImportRequestItem {
  externalRef: string;
  controlName: string;
  workpapers: OptroImportWorkpaperChoice[];
}

export const StartImportResponseSchema = z.object({
  importId: z.string(),
});

// The `detail` body of the 502 raised by `start_import` when some (but not
// all) items failed to enqueue: the import still has a valid id and is
// worth polling for the items that did enqueue.
export const OptroImportPartialEnqueueSchema = z.object({
  error: z.literal("optro_import_partial_enqueue"),
  importId: z.string(),
  enqueued: z.number(),
  failed: z.number(),
});

// A single labeled value in a Control Record section, as returned by
// `GET /controls/external/control/{controlId}/record`.
export const OptroRecordFieldSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export type OptroRecordField = z.infer<typeof OptroRecordFieldSchema>;

// A titled group of fields in a Control Record (e.g. "Ownership").
export const OptroRecordSectionSchema = z.object({
  title: z.string(),
  fields: z.array(OptroRecordFieldSchema),
  // Prose rendered instead of label/value rows (the work-step record's
  // "Relationships" section); absent on control records.
  text: z.string().nullable().optional(),
});

export type OptroRecordSection = z.infer<typeof OptroRecordSectionSchema>;

// A live, stateless snapshot of a linked control's data in Optro, fetched
// fresh on each request (nothing here is persisted). Mirrors the backend's
// `ControlRecordResponse` wire shape.
export const OptroRecordSchema = z.object({
  syncedAt: z.string(),
  optroUrl: z.string(),
  sections: z.array(OptroRecordSectionSchema),
  // Work-step records only (absent on control records): the Optro status
  // that freezes the item's testing strategy (Reviewed/Complete/Not In
  // Scope), or null when testable. Used as the disable reason copy.
  untestableStatus: z.string().nullable().optional(),
  // Work-step records only: whether the acting user holds action.edit on the
  // Optro item (from Optro's per-user _permissions hash). False disables
  // "New Test"; it already accounts for terminal statuses and the admin
  // override. Null/absent means unknown, so status-only gating applies.
  canEdit: z.boolean().nullable().optional(),
});

export type OptroRecord = z.infer<typeof OptroRecordSchema>;

// The refreshed mirrored fields for a linked control, as returned by
// `POST /controls/external/control/{controlId}/resync`. Optro is the
// source of truth for these fields, so cycle/process/controlOwner can come
// back null when Optro no longer has a value.
export const ResyncFromOptroResponseSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  cycle: z.string().nullable(),
  process: z.string().nullable(),
  controlOwner: z.string().nullable(),
});

export type ResyncFromOptroResponse = z.infer<
  typeof ResyncFromOptroResponseSchema
>;

const ATTACHMENT_PREFIX = "att-";
const FILE_PREFIX = "file-";
const SHEET_PREFIX = "sheet-";

// Maps a chosen `OptroWorkpaperFile` candidate to the import request's
// workpaper choice shape. `id` encodes the candidate's origin (see
// `OptroWorkpaperFileSchema`); uploads carry their own presigned upload id
// as `id` (see ChangeWorkpaperDialog's presigned-upload integration).
export function workpaperFileToChoice(
  file: OptroWorkpaperFile,
): OptroImportWorkpaperChoice {
  if (file.source === "upload") {
    return {
      kind: "upload",
      attachmentId: null,
      testId: null,
      uploadId: file.id,
      fileId: null,
      fileName: file.fileName,
    };
  }
  if (file.id.startsWith(SHEET_PREFIX)) {
    return {
      kind: "optro_sheet",
      attachmentId: null,
      testId: Number(file.id.slice(SHEET_PREFIX.length)),
      uploadId: null,
      fileId: null,
      fileName: file.fileName,
    };
  }
  if (file.id.startsWith(ATTACHMENT_PREFIX)) {
    return {
      kind: "optro_attachment",
      attachmentId: Number(file.id.slice(ATTACHMENT_PREFIX.length)),
      testId: null,
      uploadId: null,
      fileId: file.fileId,
      fileName: file.fileName,
    };
  }
  if (file.id.startsWith(FILE_PREFIX)) {
    return {
      kind: "optro_attachment",
      attachmentId: null,
      testId: null,
      uploadId: null,
      fileId: Number(file.id.slice(FILE_PREFIX.length)),
      fileName: file.fileName,
    };
  }
  // Unrecognized id shape from an "optro" source shouldn't happen; fall back
  // to treating it as an upload keyed by its own id so the import request is
  // still well-formed rather than silently dropping the choice.
  return {
    kind: "upload",
    attachmentId: null,
    testId: null,
    uploadId: file.id,
    fileId: null,
    fileName: file.fileName,
  };
}
